/**
 * Nút "Cài ứng dụng" trên thanh trên cùng (docs/13-tasks/01-ui-chrome.md#17 §3).
 *
 * Chrome/Edge bắn `beforeinstallprompt` khi trang đủ điều kiện cài (có manifest + service
 * worker có xử lý `fetch`). Chặn sự kiện đó lại và GIỮ nó, rồi mới có cái để `prompt()` khi
 * người dùng bấm nút — sự kiện chỉ dùng được MỘT lần, nên sau khi gọi thì bỏ đi.
 *
 * ⚠️ Safari (iOS và macOS) KHÔNG có sự kiện này: ở đó nút không bao giờ hiện, người dùng cài
 * bằng "Chia sẻ → Thêm vào Màn hình chính". Đó là lý do nút chỉ hiện khi trình duyệt đã mời —
 * một nút bấm vào không làm gì còn tệ hơn không có nút (utils/installApp.ts).
 * ⚠️ Không có cổng trình duyệt nào gác file này (luật 02 §6.3): dò tay khi sửa.
 */
import { useCallback, useEffect, useState } from 'react';

import { isInstalled, shouldOfferInstall } from '../utils/installApp.js';

/** Phần của `BeforeInstallPromptEvent` mà mã này dùng — TypeScript chưa khai kiểu đó sẵn. */
interface InstallPromptEvent extends Event {
  prompt: () => Promise<unknown>;
}

function readInstalled(): boolean {
  const standalone = window.matchMedia('(display-mode: standalone)').matches;
  const ios = (navigator as Navigator & { standalone?: boolean }).standalone;
  return isInstalled(standalone, ios);
}

export function useInstallPrompt() {
  const [pending, setPending] = useState<InstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(readInstalled);

  useEffect(() => {
    const onOffer = (event: Event) => {
      /* Không chặn thì Chrome tự hiện thanh mời cài và sự kiện mất, nút sẽ không bao giờ dùng được. */
      event.preventDefault();
      setPending(event as InstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setPending(null);
    };
    window.addEventListener('beforeinstallprompt', onOffer);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onOffer);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = useCallback(() => {
    if (pending === null) return;
    void pending.prompt();
    /* Dùng một lần là hết: giữ lại sẽ có nút bấm vào không ra gì ở lần thứ hai. */
    setPending(null);
  }, [pending]);

  return { canInstall: shouldOfferInstall(pending !== null, installed), install };
}
